import React from 'react';
import { appearanceHex } from './appearanceSchema';

/**
 * Semantic UI aliases that expand to multiple Bootstrap utility classes.
 * Admin-side UI options use short codes; here they map to the real classes.
 */
export interface ResolvedUi {
    className: string;
    style: React.CSSProperties;
    /** Classes for the `<span class="badge">` wrapped around a cell value. */
    badgeClass: string;
    /** Undefined inherits the badge; text explicitly removes it. */
    badgeMode?: 'badge' | 'text';
    /** Inline colours for that badge when the palette colour has no Bootstrap class. */
    badgeStyle: React.CSSProperties;
    /** Table header of a column: alignment and width. */
    headerClassName: string;
    headerStyle: React.CSSProperties;
    /** Form field group: grid width. */
    groupClassName: string;
    /** `variant: short` - the cell shows a cut-down value and the full text on hover. */
    truncate?: boolean;
}

const empty = (): ResolvedUi => ({
    className: '', style: {}, badgeClass: '', badgeStyle: {}, headerClassName: '', headerStyle: {}, groupClassName: '',
});

/**
 * Separates UI values into CSS classes and inline styles.
 * Values starting with '#' are treated as text colors, everything else as CSS classes.
 *
 * `badge-*` aliases don't expand into the cell's className — Bootstrap's
 * `.badge` only renders correctly on an inline element. They expand into
 * `badgeClass` instead, which the cell renderer applies to a wrapping
 * `<span class="badge ...">` around the value.
 */
export type Appearance = Record<string, string[]> | null | undefined;

/** The values of one key as a list, however the server or a test wrote them. */
const valuesOf = (appearance: Appearance, key: string): string[] => {
    const raw = appearance?.[key] as unknown;
    if (raw === null || raw === undefined || raw === '') {
        return [];
    }
    return (Array.isArray(raw) ? raw : [raw]).map(String);
};

/** The one value a single-valued key carries; the first one wins when several were set. */
const firstOf = (appearance: Appearance, key: string): string | undefined => valuesOf(appearance, key)[0];

/** A screen whose rows are a conversation: drawn as chat bubbles with a composer instead of a table. */
export const isCommentsScreen = (screen: { appearance?: Appearance } | null | undefined): boolean =>
    valuesOf(screen?.appearance, 'variant').includes('comments');

export type CommentPart = 'body' | 'caption' | 'attachment';

const COMMENT_PARTS: CommentPart[] = ['body', 'caption', 'attachment'];

const commentPartOf = (appearance: Appearance): CommentPart | null => {
    const variants = valuesOf(appearance, 'variant');
    return COMMENT_PARTS.find(part => variants.includes(`comment-${part}`)) ?? null;
};

/**
 * What a column is inside a comment bubble, as marked in admin through the
 * column's appearance (`variant: comment-body` …). A column without a part is
 * not shown in the bubble at all - nothing is guessed from types or order.
 */
export const commentPart = (column: { appearance?: Appearance } | null | undefined): CommentPart | null =>
    commentPartOf(column?.appearance);

/** A column carries the row's name when its variant says so - the successor of the `title` UI code. */
export const isTitleColumn = (column: { appearance?: Appearance } | null | undefined): boolean =>
    valuesOf(column?.appearance, 'variant').includes('title');

export type UiComponent = 'column' | 'rule' | 'button' | 'formField' | 'screen' | 'form';

/** Palette codes that Bootstrap/Tabler already has classes for (`bg-*`, `btn-*`, `btn-outline-*`). */
const BOOTSTRAP_COLORS: Record<string, string> = {
    primary: 'primary', success: 'success', danger: 'danger', warning: 'warning', muted: 'secondary',
    secondary: 'secondary', info: 'info', dark: 'dark', light: 'light',
};

/** Semantic names the theme already defines; any other colour value is a hex used as it is. */
const SEMANTIC_VARS: Record<string, string> = {
    primary: '--tblr-primary', success: '--tblr-success', danger: '--tblr-danger', warning: '--tblr-warning',
    muted: '--tblr-secondary', secondary: '--tblr-secondary', info: '--tblr-info', dark: '--tblr-dark', light: '--tblr-light',
};

/**
 * The CSS colour for a `color` or `background` value: a hex as it is, the
 * theme variable for a semantic name, else nothing - a value the client
 * cannot paint is simply skipped.
 */
const paint = (code: string): string | undefined =>
    appearanceHex(code) ?? (SEMANTIC_VARS[code] ? `var(${SEMANTIC_VARS[code]})` : undefined);

/** Whether a white label would be unreadable on this colour. */
const isLightColor = (code: string): boolean => {
    const hex = appearanceHex(code);
    if (hex) {
        const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
        return 0.299 * r + 0.587 * g + 0.114 * b > 0.6;
    }
    return code === 'warning' || code === 'light';
};

/**
 * Turns an `appearance` object into classes and styles for one component.
 * A colour is a hex or a Bootstrap name. Keys the client does not know are ignored.
 */
export const resolveAppearance = (appearance: Appearance, component: UiComponent): ResolvedUi => {
    const result = empty();
    if (!appearance || typeof appearance !== 'object') {
        return result;
    }

    const classes: string[] = [];
    const headerClasses: string[] = [];
    const color = firstOf(appearance, 'color');
    const background = firstOf(appearance, 'background');
    const variants = valuesOf(appearance, 'variant');
    const styles = valuesOf(appearance, 'style');
    const align = firstOf(appearance, 'align');
    const responsive = firstOf(appearance, 'responsive');

    if (component === 'button') {
        // A button is drawn as one Bootstrap variant: outline unless told otherwise.
        const solid = variants.includes('solid');
        const tone = color ?? (solid || variants.includes('outline') ? 'primary' : undefined);
        if (tone) {
            const bootstrap = BOOTSTRAP_COLORS[tone];
            const hex = bootstrap ? undefined : paint(tone);
            if (bootstrap) {
                classes.push(solid ? `btn-${bootstrap}` : `btn-outline-${bootstrap}`);
            } else if (hex && solid) {
                result.style.backgroundColor = hex;
                result.style.borderColor = hex;
                result.style.color = isLightColor(tone) ? '#1a2230' : '#fff';
            } else if (hex) {
                result.style.color = hex;
                result.style.borderColor = hex;
            }
        }
        const backgroundHex = background ? paint(background) : undefined;
        if (backgroundHex) {
            result.style.backgroundColor = backgroundHex;
        }
    } else {
        const isPill = variants.includes('pill');
        const isBadge = isPill || variants.includes('badge');
        if (isBadge || variants.includes('text')) {
            result.badgeMode = isBadge ? 'badge' : 'text';
        }
        if (isBadge) {
            // The badge takes the colours; the cell itself stays as it is.
            const badgeTone = background ?? color ?? 'primary';
            const bootstrap = BOOTSTRAP_COLORS[badgeTone];
            const badgeClasses = ['badge'];
            if (isPill) {
                badgeClasses.push('rounded-pill');
            }
            if (bootstrap) {
                badgeClasses.push(`bg-${bootstrap}`);
            } else {
                const hex = paint(badgeTone);
                if (hex) {
                    result.badgeStyle.backgroundColor = hex;
                }
            }
            const labelHex = background && color ? paint(color) : undefined;
            if (labelHex) {
                result.badgeStyle.color = labelHex;
            } else {
                badgeClasses.push(isLightColor(badgeTone) ? 'text-dark' : 'text-white');
            }
            result.badgeClass = badgeClasses.join(' ');
        } else {
            const colorHex = color ? paint(color) : undefined;
            if (colorHex) {
                result.style.color = colorHex;
            }
            const backgroundHex = background ? paint(background) : undefined;
            if (backgroundHex) {
                result.style.backgroundColor = backgroundHex;
            }
        }
    }

    if (variants.includes('short') && component !== 'button') {
        result.truncate = true;
    }
    // `desktop` keeps the element for md-and-up, `mobile` for below md; a table cell needs its own display value.
    if (responsive === 'desktop' || responsive === 'mobile') {
        const cell = component === 'column' || component === 'rule';
        const shown = cell ? 'd-md-table-cell' : 'd-md-block';
        const responsiveClass = responsive === 'desktop' ? `d-none ${shown}` : 'd-md-none';
        classes.push(responsiveClass);
        if (component === 'column') {
            headerClasses.push(responsiveClass);
        }
    }
    if (styles.includes('bold')) {
        classes.push('fw-bold');
    }
    if (styles.includes('italic')) {
        classes.push('fst-italic');
    }
    const alignmentClass = align === 'left' ? 'text-start'
        : align === 'right' ? 'text-end'
        : align === 'center' ? 'text-center' : undefined;
    if (alignmentClass) {
        classes.push(alignmentClass);
        headerClasses.push(alignmentClass);
    }
    // On a comments screen the column's part of the bubble is its own class - see commentPart().
    const part = commentPartOf(appearance);
    if (part && component === 'column') {
        classes.push(`comment-${part}`);
    }

    result.className = classes.join(' ');
    result.headerClassName = headerClasses.join(' ');
    return result;
};

const joinClasses = (...parts: string[]): string =>
    Array.from(new Set(parts.join(' ').split(' ').filter(Boolean))).join(' ');

// Explicit alignment overrides an older UI alignment regardless of CSS rule order.
const mergeAlignmentClasses = (base: string, override: string): string => {
    const isAlignment = (value: string): boolean => /^text-(left|right|start|center|end)$/.test(value);
    const baseClasses = override.split(/\s+/).some(isAlignment)
        ? base.split(/\s+/).filter(value => !isAlignment(value)).join(' ') : base;
    return joinClasses(baseClasses, override);
};

/** Combines two resolutions; the second one wins where both set the same thing. */
export const mergeUi = (base: ResolvedUi, override: ResolvedUi): ResolvedUi => ({
    className: mergeAlignmentClasses(base.className, override.className),
    style: { ...base.style, ...override.style },
    badgeMode: override.badgeMode ?? (override.badgeClass ? 'badge' : base.badgeMode),
    badgeClass: override.badgeMode === 'text' ? '' : override.badgeClass || base.badgeClass,
    badgeStyle: override.badgeMode === 'text' ? {}
        : override.badgeClass ? override.badgeStyle : { ...base.badgeStyle, ...override.badgeStyle },
    headerClassName: mergeAlignmentClasses(base.headerClassName, override.headerClassName),
    headerStyle: { ...base.headerStyle, ...override.headerStyle },
    groupClassName: override.groupClassName || base.groupClassName,
    truncate: override.truncate ?? base.truncate,
});

/** The usual pairing: the free-form `ui` codes first, the appearance on top. */
